import { useCallback, useMemo } from "react";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

// Type for storing touchpoint type selections by person
type TouchpointTypeSelections = Record<string, string[] | undefined>;

type UpdateOptions = {
  intentionalClear?: boolean;
};

export function useTouchpointTypeSelection(personId: string | null) {
  // Use a single standardized key for all touchpoint type selections
  const [allSelections, setAllSelections] =
    useTrackedSessionStorage<TouchpointTypeSelections>(
      "salespeople::explorer::touchpoint-types::filters",
      {},
      {
        migration: {
          from: {
            key: "salespeople::explorer::selected-touchpoint-types",
            storage: "localStorage",
            organizationScoped: true,
          },
          cleanup: true,
        },
      },
    );

  // Page-level auto flag for touchpoint types
  const [auto, setAuto] = useTrackedSessionStorage<boolean>(
    "salespeople::explorer::touchpoint-types::auto",
    true,
  );

  // Get the selected types for this person (or default)
  const selectedTypes = useMemo(() => {
    const key = personId || "default";
    return allSelections[key] || [];
  }, [allSelections, personId]);

  // Whether there is an explicit stored selection for this person (including intentional empty)
  const hasOverride = useMemo(() => {
    const key = personId || "default";
    return Object.hasOwn(allSelections || {}, key);
  }, [allSelections, personId]);

  const selectTypes = useCallback(
    (types: string[], options?: UpdateOptions) => {
      const key = personId || "default";
      setAllSelections((prev) => {
        const base = prev || {};
        if (options?.intentionalClear) {
          return { ...base, [key]: [] };
        }
        return { ...base, [key]: types };
      });
    },
    [personId, setAllSelections],
  );

  const toggleType = useCallback(
    (type: string) => {
      const key = personId || "default";
      setAllSelections((prev) => {
        const safePrev = prev || {};
        const currentTypes = safePrev[key] || [];
        const newTypes = currentTypes.includes(type)
          ? currentTypes.filter((t) => t !== type)
          : [...currentTypes, type];

        return {
          ...safePrev,
          [key]: newTypes,
        };
      });
    },
    [personId, setAllSelections],
  );

  const selectAll = useCallback(
    (allTypes: string[]) => {
      const key = personId || "default";
      setAllSelections((prev) => ({
        ...(prev || {}),
        [key]: allTypes,
      }));
    },
    [personId, setAllSelections],
  );

  const clearAll = useCallback(() => {
    const key = personId || "default";
    setAllSelections((prev) => ({
      ...(prev || {}),
      [key]: [],
    }));
  }, [personId, setAllSelections]);

  const clearAllOverrides = useCallback(() => {
    // Wipe the filters key entirely (no overrides)
    setAllSelections({});
  }, [setAllSelections]);

  return {
    selectedTypes,
    hasOverride,
    selectTypes,
    toggleType,
    selectAll,
    clearAll,
    handleTypesChange: selectTypes, // Alias for consistency with campaign/channel selectors
    auto,
    setAuto,
    clearAllOverrides,
  };
}
